/** @module csi/widget/GridSelect */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"dijit/_Container",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/_WidgetBase",
	"dgridNew/OnDemandGrid",
	"dgridNew/Selection",
	"dgridNew/Selector",
	"dgridNew/Tree",
	"underscore",
	"dojo/text!./GridSelect.html",
	
    "csi/widget/ValidationTextBox"
], function(declare,aspect,_Container,_TemplatedMixin,_WidgetsInTemplateMixin,_WidgetBase,OnDemandGrid,Selection,Selector,Tree,_,template) {

	// TODO: Handle onAllowSelect - don't style as if it can be selected
	// TODO: Testing for multi-select case
	// TODO: Keyboard support?

	const DEFAULT_GRID_PARAMS = {
		cellNavigation: false,
		showHeader: false,
		showFooter: false
	};
	
	var _GridSelect = declare([_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin,_Container], {
		baseClass: "csi-grid-select",
		customErrorHandling: true,
		templateString: template,

		/** @property {_WidgetBase} formValidationNode - child widget that handles validation for overall widget */
		formValidationNode: null,
		
		/** @property {OnDemandGrid} grid - grid instance */
		grid: null,
		
		/** @property {Object[]} gridMixins - mixins to use with the grid */
		gridMixins: [Selection],
		
		/** @property {DOMNode} gridNode - node where to insert grid */
		gridNode: null,
		
		/** @property {Object} gridParams - parameters to use to instantiate grid; */
		gridParams: DEFAULT_GRID_PARAMS,
		
		/** @property {boolean} expandFirstLevel - indicates if should expand first level of grid if working with tree */
		expandFirstLevel: false,
		
		/** @property {boolean} isTree - indicates if store is hierarchical */
		isTree: false,
		
		/** @property {string} nameProperty - property of item in store to display in grid */
		nameProperty: "name",
		
		/** @property {boolean} required - set to true if selection is required */
		required: false,
		
		/** @property {boolean} singleSelect - set to true if can only select one item */
		singleSelect: true,

		/** @property {dMemory} store - data store for grid (dstore/Memory) */
		store: null,
		
		/** @property {TextBox} textbox - text box used to filter grid */
		textbox: null,
		
		/** @property {number} timeoutDelay - time in milliseconds from when text last entered to filter by input */
		timeoutDelay: 300,

		/** @property {string|string[]} value - single or multiple selected store ids based on if single selection or not */
		value: "",
		
		/** @property {Object<string,boolean>} _autoExpandIds - map of ids to auto-expand to reach filter matches */
		_autoExpandIds: null,
		
		/** @property {boolean} _disableSelect - if true, then grid selection is sync'ing with value and should not signal a change */
		_disableSelect: false,
		
		/** @property {boolean} _firstTimeResize - only true the first time the widget is resized with the grid created */
		_firstTimeResize: true,
		
		/** @property {Object} _timeoutOnBlur - handle for timeout from last blur */
		_timeoutOnBlur: null,
		
		/** @property {Object} _timeoutOnChange - handle for timeout from last entered text value */
		_timeoutOnChange: null,
		
		/**
		 * Focus on text box
		 */
		focus: function() {
			this.textbox.focus();
		},
		
		/**
		 * Return selected item(s) in grid
		 * @return {Object|Object[]}
		 */
		getSelection: function() {
			const store = this.store;
			const value = this.value;

			if (this.singleSelect) {
				return store.getSync(value);
			}
			
			return _.map(this.value, function(id) {
				return store.getSync(id);
			});
		},
		
		/**
		 * Extension point called to determine if given item can be selected; by default allow all to be selected
		 * @param {Object} item - item from data store
		 * @return {boolean}
		 */
		onAllowSelect: function(item) { return true; },
		
		/**
		 * Extension point called when grid selection changes
		 * @param {string|string[]} value - selected values
		 */
		onChange: function(value) { },
		
		/**
		 * Extension point called to create store filter 
		 * @param {dstore/Filter} filter
		 * @param {string} text - search text value
		 * @return {dstore/Filter}
		 */
		onCreateFilter: function(filter, text) {
			return filter.match(this.nameProperty, new RegExp(text, "i"));
		},
		
		/**
		 * @override
		 * Create grid
		 */
		postCreate: function() {
			var self = this;

			this.inherited(arguments);
			
			const mixins = _.clone(this.gridMixins);
			
			if (this.isTree) {
				mixins.push(Tree);
			}
			
			const params = {
				allowSelect: this._onAllowSelect.bind(this),
				deselectOnRefresh: false,
				selectionEvents: []
			};
			
			if (this.gridParams) {
				_.extend(params, this.gridParams);
			}
			
			// expand to single selection if a tree
			if (!params.shouldExpand && this.isTree) {
				params.shouldExpand = this._onShouldExpand.bind(this);
			}
			
			// set selection mode if not specified
			if (!params.selectionMode) {
				params.selectionMode = this.singleSelect ? "none" : "extended";
			}
			
			// add single column with name property if columns not specified
			if (!params.columns) {
				params.columns = [
					{field: "_X_", className: this.baseClass + "-checkmark"},
					{field: this.nameProperty}
				];

				if (this.isTree) {
					params.columns[1].renderExpando = true;
				}
			}
			
			// create grid and setup event handling
			const grid = this.grid = new declare([OnDemandGrid].concat(mixins))(params, this.gridNode);
			grid.on("dgrid-select", this._onGridSelect.bind(this));
			grid.on("dgrid-deselect", this._onGridSelect.bind(this));
			grid.on("dgrid-refresh-complete", this._onGridRefresh.bind(this));
			
			// override select logic by setting selectionEvents above to empty array, and processing it in row click event;
			// clicking on the expando icon was also selecting the row, and couldn't find a way to stop that other than this
			grid.on(".dgrid-row:click", _.noop);
			grid.on(".dgrid-cell:click", this._onGridCellClick.bind(this));
			
			grid.startup();
			
			// below is needed for validation along with _ErrorMessageMixin
			this.formValidationNode = this.textbox;
		},

		/**
		 * Refresh the grid when form is resized. This is mainly needed for the shouldExpand logic to work correctly,
		 * as that only works once the grid is displayed (has size). If it is hidden and being rendered in the background,
		 * the return value of shouldExpand does not matter.
		 */
		resize: function() {
			const grid = this.grid;
			
			if (grid) {
				if (this._firstTimeResize) {
					this._firstTimeResize = false;
					grid.refresh();
				} else {
					grid.resize();
				}
			}
		},
		
		/**
		 * @override
		 * Stop timers
		 */
		uninitialize: function() {
			clearTimeout(this._timeoutOnBlur);
			clearTimeout(this._timeoutOnChange);
			this.inherited(arguments);
		},
		
		/**
		 * Validate widget
		 * @param {boolean} isFocused
		 * @return {boolean}
		 */
		validate: function(isFocused) {
			return this.textbox.validate(isFocused);
		},
		
		/**
		 * Restore selection value to textbox, and reset any filters
		 */
		_blur: function() {
			if (this._resetTextBoxToValue()) {
				this._resetGridCollection(this.store);
			}
		},
		
		/**
		 * Filter grid contents by current text value
		 */
		_filterGrid: function() {
			const self = this;
			const store = this.store;
			const text = this.textbox.get("value");
			
			if (!text) {
				this._resetGridCollection(store);
				return;
			}
			
			const textFilter = this.onCreateFilter(new store.Filter(), text);
			const expandIds = {};
			
			var filteredCollection = store.filter(textFilter);
			
			if (this.isTree) {
				// need to ensure that parent and child items are included in collection,
				// even though they do not match the filter
				const obj = {};
				
				function hash(item) {
					obj[store.getIdentity(item)] = 1;
				}
				
				function addChildren(item) {
					store.getChildren(item).forEach(function(child) {
						hash(child);
						addChildren(child);
					});
				}
				
				filteredCollection.forEach(function(item) {
					if (!self.onAllowSelect(item)) {
						return;
					}
					
					addChildren(item);
	
					while (item) {
						hash(item);
						item = store.getSync(item.parent);
						
						if (item) {
							expandIds[store.getIdentity(item)] = true;
						}
					}
				});
				
				filteredCollection = store.filter(function(item) {
					return obj[store.getIdentity(item)];
				});

				// need to make this collection its own root so that tree children include only filtered items
				filteredCollection.root = filteredCollection;
			}

			this._resetGridCollection(filteredCollection);
			this._autoExpandIds = expandIds;
		},
		
		/**
		 * Return true if there is no selection
		 * @return {boolean}
		 */
		_hasNoSelection: function() {
			return _.isEmpty(this.value);
		},
		
		/**
		 * Return true if at least one item is selected
		 * @return {boolean}
		 */
		_hasSelection: function() {
			return !_.isEmpty(this.value);
		},
		
		/**
		 * Call extension point to determine if row can be selected
		 * @param {Object} row
		 * @return {boolean}
		 */
		_onAllowSelect: function(row) {
			var data = row.data;
			
			if (!data && this.store && row.id) {
				data = this.store.getSync(row.id);
			}
			
			return data ? this.onAllowSelect(data) : false;
		},

		/**
		 * Restore selection if it exists
		 */
		_onBlurTextBox: function() {
			this._timeoutOnBlur = setTimeout(this._blur.bind(this), 100);
		},
		
		/**
		 * Filter grid based on new text value after some timeout (pause in typing)
		 */
		_onChangeTextBox: function(value) {
			clearTimeout(this._timeoutOnChange);
			this._timeoutOnChange = setTimeout(this._filterGrid.bind(this), this.timeoutDelay);
		},

		/**
		 * Store onBlur logic if textbox is focused 
		 */
		_onFocusTextBox: function() {
			clearTimeout(this._timeoutOnBlur);
		},
		
		/**
		 * Handle cell click event
		 * @param {Object} event
		 */
		_onGridCellClick: function(event) {
			const grid = this.grid;
			const cell = grid.cell(event);
			const row = cell.row;
			const canSelect = this._onAllowSelect(row);
			const bExpand = $(event.target).hasClass("dgrid-expando-icon");
			
			// do nothing if expando icon clicked
			if (!bExpand) {
				if ((cell.column.field == this.nameProperty) || !canSelect) {
					// expand/collapse row if click on second column or not allowed to select the cell
					grid.expand(row);
				} else if (this.singleSelect) {
					// if row is already selected, then deselect; otherwise clear other selections and select
					const isSelected = grid.isSelected(row);
					
					grid.clearSelection();
					
					if (!isSelected) {
						grid.select(row);
					}
				}
            }
			
			this.textbox.focus();
		},

		/**
		 * Select row(s) in grid according to current value
		 * @param {Object} event
		 */
		_onGridRefresh: function(event) {
			this._resetGridSelection();
		},
		
		/**
		 * Set value of widget when selection changes
		 * @param {Object} event
		 */
		_onGridSelect: function(event) {
			if (this._disableSelect) {
				return;
			}

			const selection = this.grid.get("selection");
			const values = [];
			
			for (var id in selection) {
				if (selection[id]) {
					values.push(id);
				}
			}

			const value = this.singleSelect ? values[0] : values;
			
			if (!_.isEqual(value, this.value)) {
				this._disableSelect = true;
				this.set("value", value);
				this._disableSelect = false;
			}
			
			this.textbox.focus();
		},
		
		/**
		 * Return true if given row should be expanded on load
		 * @param {Object} row
		 * @param {number} level
		 * @param {boolean} previouslyExpanded
		 * @return {boolean}
		 */
		_onShouldExpand: function(row, level, previouslyExpanded) {
			if (previouslyExpanded) {
				return true;
			}
			
			if (this._autoExpandIds && this._autoExpandIds[row.id]) {
				return true;
			}

			if (this.expandFirstLevel && !level) {
				return true;
			}

			if (this.singleSelect && this.value) {
				const store = this.store;
				const data = row.data;
				var selection = this.getSelection();
				
				while (selection) {
					selection = store.getSync(selection.parent);
					
					if (selection === data) {
						return true;
					}
				}
			}
			
			return false;
		},

		/**
		 * Set collection on grid from given store
		 * @param {Store} store
		 */
		_resetGridCollection: function(store) {
			this._autoExpandIds = null;
			this.grid.set("collection", this.isTree ? store.getRootCollection() : store);
		},
		
		/**
		 * Reset selection on grid to current value
		 */
		_resetGridSelection: function() {
			const grid = this.grid;

			if (grid && this.store) {
				// disable grid when sync'ing selection with value so we don't get select event
				const values = this.value ? [].concat(this.value) : [];
	
				this._disableSelect = true;
				
				if (values.length) {
					_.each(values, function(id) {
						grid.select(grid.row(id));
					});
				} else {
					grid.clearSelection();
				}

				this._disableSelect = false;
			}
		},
		
		/**
		 * Update text box with name of selected item, if any (currently only works in single select mode)
		 * @return {boolean} - return true if the text box was changed
		 */
		_resetTextBoxToValue: function() {
			if (this.store) {
				const item = this.getSelection();
				const textbox = this.textbox;
				
				if (this.singleSelect && (!!item == !!this.value)) {
					// only change if there is an item for the current value, if any
					const text = item ? item[this.nameProperty] : "";

					if (text != textbox.get("value")) {
						textbox._onChangeActive = false;
						textbox.set("value", text);
						textbox._onChangeActive = true;
						return true;
					}
				} else {
					// TODO: not yet implemented for multi-select
				}
			}
			
			return false;
		},
		
		/**
		 * Forward required flag to textbox
		 * @param {boolean} value
		 */
		_setRequiredAttr: function(value) {
			this._set("required", value);
			this.textbox.set("required", value);
		},
		
		/**
		 * Pass store onto grid
		 * @param {dMemory} value
		 */
		_setStoreAttr: function(value) {
			this._set("store", value);
			this._resetGridCollection(value);
			this._resetTextBoxToValue();
		},
		
		/**
		 * Pass store onto grid
		 * @param {dMemory} value
		 */
		_setValueAttr: function(value) {
			if (!_.isEqual(value, this.value)) {
				this._set("value", value);
				this._resetTextBoxToValue();
				this._resetGridSelection();
				
				if (this._started) {
					this.onChange(value);
				}
			}
		}
	});
	
	_GridSelect.DEFAULT_GRID_PARAMS = DEFAULT_GRID_PARAMS;
	
	return _GridSelect;
});